import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterModule, RouterOutlet } from '@angular/router';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalModule, MsalService } from '@azure/msal-angular';
import { AuthenticationResult, EventMessage, EventType, InteractionStatus, PopupRequest, RedirectRequest, SilentRequest } from '@azure/msal-browser';
import { Subject, Subscription, filter, takeUntil } from 'rxjs';
import { CfMenuComponent } from './ui/cf-menu/cf-menu.component';
import { CFButtonType, CfButtonComponent } from './ui/cf-button/cf-button.component';
import { UserService } from '@aifs-shared/user/user.service';
import { User, UserDataResponse } from '@aifs-shared/user/user';
import { AuthenticationService } from '@aifs-shared/auth/authentication.service';
import { StorageWrapperService } from '@aifs-shared/cache/storage-wrapper.service';
import { CFLoaderSize, CFLoaderStyle, CfLoaderComponent } from './ui/cf-loader/cf-loader.component';
import { UiModalService } from './ui/ui-modal.service';
import { CFModalButton } from './ui/cf-modal/cf-modal.component';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
    selector: 'app-root',
    imports: [CommonModule, MsalModule, RouterModule, RouterOutlet, CfButtonComponent, CfLoaderComponent, CfMenuComponent],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
    // MARK: Show MSAL Debug
    showMSALDebugInfo = false;

    Button = CFButtonType;
    Loader = CFLoaderStyle;
    Size = CFLoaderSize;

    constructor(
        @Inject(MSAL_GUARD_CONFIG)
        private msalGuardConfig: MsalGuardConfiguration,
        private msalAuthService: MsalService,
        private msalBroadcastService: MsalBroadcastService,
        private router: Router,
        private route: ActivatedRoute,
        private authService: AuthenticationService,
        private userService: UserService,
        private modal: UiModalService,
        private storage: StorageWrapperService
    ) { }


    ngOnInit(): void {
        this.updateMenuVisibility();
        this.router.events
            .pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                takeUntil(this._destroying$)
            )
            .subscribe(() => {
                this.updateMenuVisibility();
            });

        this.userChangeSubscription = this.userService.userChanged.subscribe({
            next: (u: User | undefined) => {
                // if (u) console.log(`Subscription Changed: ${u.id}`);
                // else console.log(`User cleared`);

                this.user = u;
            }
        });

        this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/'
        if (this.returnUrl.toLowerCase().startsWith('http')) {
            // Do not permit open redirects (IAS)
            this.returnUrl = '/';
        }

        this.msalAuthService.handleRedirectObservable().subscribe();
        this.isIframe = window !== window.parent && !window.opener; // Remove this line to use Angular Universal

        this.user = this.userService.user();
        this.setLoginDisplay();

        // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED 
        // events emitted when a user logs in or out of another tab or window
        this.msalAuthService.instance.enableAccountStorageEvents();
        this.msalBroadcastService.msalSubject$
            .pipe(
                filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED),
            )
            .subscribe({
                next: (result: EventMessage) => {
                    if (this.msalAuthService.instance.getAllAccounts().length === 0) {
                        window.location.pathname = "/";
                    } else {
                        // this.setLoginDisplay();
                        this.initialiseAndCheckAccount();
                    }
                }
            });

        this.msalBroadcastService.inProgress$
            .pipe(
                filter((status: InteractionStatus) => status === InteractionStatus.None),
                takeUntil(this._destroying$)
            )
            .subscribe(() => {
                if (this.showMSALDebugInfo) console.log(`inProgress?`)
                // this.setLoginDisplay();
                // this.checkAndSetActiveAccount();
                this.initialiseAndCheckAccount();
            })
    }

    ngOnDestroy(): void {
        this.userChangeSubscription?.unsubscribe();

        this._destroying$.next(undefined);
        this._destroying$.complete();
    }

    // MARK: Menu Handling
    onMenuSelected(item: string): void {
        console.log(`Got: ${item}`);
        switch (item) {
            case 'sign-out': {
                const b: CFModalButton[] = [
                    { id: 'Y', text: 'Yes', type: CFButtonType.Default },
                    { id: 'N', text: 'No', type: CFButtonType.Danger },
                ];
                this.modal
                    .showModalConfirmation('Sign Out', 'Are you sure you want to sign out?', b)
                    .subscribe({
                        next: (value: string) => {
                            if (value === 'Y') {
                                this.logout();
                            }
                        }
                    });
            }
                break;
            default:
                console.log(`Navigating to ${item}`);
                this.router.navigateByUrl(item);
                break;
        }
    }

    // MARK: Tour?

    // MARK: Azure Sign-In Logic

    initialiseAndCheckAccount(): void {
        this.msalAuthService.initialize().subscribe({
            complete: () => {
                if (this.showMSALDebugInfo) console.log('initialise completed');
                this.syncAzureLoginWithCALogin();
            }
        });
    }

    syncAzureLoginWithCALogin() {
        //if not logged in azure
        //console.log(`syncAzureLoginWithCALogin`);

        // NOTE: we don't want to limit this just to aifs.co.uk accounts
        // SEE: CA-3642
        // only @aifs.co.uk accounts
        // const azureLoggedIn = this.msalAuthService.instance.getAllAccounts().filter(el => el.username.toUpperCase().substring(el.username.length - 11, el.username.length) == '@AIFS.CO.UK').length > 0;
        const azureLoggedIn = this.msalAuthService.instance.getAllAccounts().length > 0;
        let activeAzureAccount = this.msalAuthService.instance.getActiveAccount();
        if (this.showMSALDebugInfo) console.info(activeAzureAccount);
        // console.log(this.msalAuthService.instance.getAllAccounts());
        //

        if (azureLoggedIn && activeAzureAccount == null) {
            this.msalAuthService.instance.setActiveAccount(this.msalAuthService.instance.getAllAccounts()[0])
        }
        activeAzureAccount = this.msalAuthService.instance.getActiveAccount();
        // console.log(`Active account:`,activeAzureAccount);

        if (!azureLoggedIn && activeAzureAccount == null) {
            console.log(`Not logged in, no activeAzureAccount`);
            if (!this.waitTimeDone) {
                // console.log('waiting...');
                new Promise(vars => setTimeout(vars, 2000))
                    .then(() => {
                        this.waitTimeDone = true;
                        this.syncAzureLoginWithCALogin();
                    })
            }
            else {
                console.log('aint no more waiting');
                this.msalAuthService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest)
            }
            return;
        }

        //if logged in but we haven't logged him into CA yet, or we have a wrong user logged in
        const activeCAUser = this.userService.user();
        // console.log(`Active User: ${activeAzureAccount}, ${azureLoggedIn}, ${activeCAUser}`);
        if (activeAzureAccount != null
            && azureLoggedIn
            && (activeCAUser == null)) {
            // console.log(`Got an active account, and azure is logged in`);
            if (!activeCAUser) {
                // console.log(`Don't have an active CA user`);
                this.loginDone = false;

                this.msalAuthService.acquireTokenSilent({} as SilentRequest)
                    .subscribe({
                        next: (response: AuthenticationResult) => {
                            // console.log(`Token ACQ`);
                            this.msalAuthService.instance.setActiveAccount(response.account);
                            this.signInAzureUser(response.accessToken);
                        },
                        error: (error: any) => {
                            this.msalAuthService.acquireTokenRedirect({} as SilentRequest);
                        }
                    });
                return;
            }
        }

        this.user = this.userService.user();
        // console.log(`User is ${this.user?.firstName}`);
        this.setLoginDisplay();
    }

    // logout() {
    //     localStorage.clear();
    //     this.authService.logout();
    //     this.msalAuthService.logout();
    // }

    signInAzureUser(accessToken: string) {
        // console.log('signInAzureUser', accessToken);
        this.userService
            .loginAzure(accessToken)
            .subscribe({
                next: (success: UserDataResponse) => {
                    // console.debug(`got azure user: ${JSON.stringify(success)}`);
                    this.sendingRequest = false;
                    this.loginDone = true;
                    const user = this.userService.user();
                    if (!user) throw Error(`Did not receive a valid user object!`);

                    if (success.error) {
                        const msg = success.message ? success.message : 'OOS-001';
                        this.modal.showModalError("Sign In", `The server returned an error attempting to sign you in.<br><br>Please contact support with the following message:<br><br>${msg}`);
                    } else {
                        // console.log(`Got valid Azure signin`);
                        const redirect = sessionStorage.getItem('redirectUrl');
                        if (redirect) {
                            console.log(`Got a redirect: ${redirect}`);
                            this.router.navigateByUrl(redirect);
                        } else {
                            this.setLoginDisplay();
                        }
                    }
                },
                error: (error: any) => {
                    // something unexpected occured
                    let msg = error;
                    this.emailAssigned = false;
                    this.sendingRequest = false;
                    this.loginDone = true;
                    if (error instanceof HttpErrorResponse) {
                        // you could extract more info about the error if you want, e.g.:
                        msg = `${error.statusText} (${error.status})`;
                        if (error.status !== 0) {
                            // errMsg = ...
                            this.modal.showModalError(
                                "Sign In",
                                `The server returned an error attempting to sign you in.<br><br>Please contact support with the following message:<br><br>${msg}`)
                                .subscribe({
                                    next: (result: any) => {
                                        this.router.navigateByUrl('user/user-email-not-assigned');
                                        return;
                                    }
                                });
                        }
                    }
                    this.router.navigateByUrl('user/user-email-not-assigned');
                }
            });
    }

    setLoginDisplay() {
        // console.log(`setLoginDisplay: User is ${this.user?.id}`);
        this.loginDisplay = this.msalAuthService.instance.getAllAccounts().length > 0 && this.user !== undefined;
        this.loginDone = this.user !== undefined;
    }

    private updateMenuVisibility(): void {
        const deepestRoute = this.getDeepestChildRoute(this.router.routerState.root);
        this.showTopMenu = !(deepestRoute.snapshot.data['hideTopMenu'] === true);
    }

    private getDeepestChildRoute(route: ActivatedRoute): ActivatedRoute {
        let current = route;
        while (current.firstChild) {
            current = current.firstChild;
        }
        return current;
    }

    // checkAndSetActiveAccount() {
    //     console.log(`checkAndSetActiveAccount`);

    //     /**
    //      * If no active account set but there are accounts signed in, sets first account to active account
    //      * To use active account set here, subscribe to inProgress$ first in your component
    //      * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
    //      */
    //     let activeAccount = this.msalAuthService.instance.getActiveAccount();

    //     //only @aifs.co.uk accounts
    //     const azureLoggedIn = this.msalAuthService.instance.getAllAccounts().filter(el => el.username.toUpperCase().substring(el.username.length - 11, el.username.length) == '@AIFS.CO.UK').length > 0;

    //     if (!activeAccount && this.msalAuthService.instance.getAllAccounts().length > 0) {
    //         let accounts = this.msalAuthService.instance.getAllAccounts();
    //         this.msalAuthService.instance.setActiveAccount(accounts[0]);

    //     }
    //     // if logged in but we haven't logged him into CA yet, or we 
    //     // have a wrong user logged in
    //     const activeCAUser = this.userService.user();
    //     console.log(`Active User: ${activeAccount}, ${azureLoggedIn}, ${activeCAUser}`);
    //     if (activeAccount != null
    //         && azureLoggedIn
    //         && (activeCAUser == null)) {
    //         console.log(`Got an active account, and azure is logged in`);
    //         if (!activeCAUser) {
    //             console.log(`Don't have an active CA user`);
    //             this.loginDone = false;

    //             this.msalAuthService.acquireTokenSilent({} as SilentRequest)
    //                 .subscribe({
    //                     next: (response: AuthenticationResult) => {
    //                         console.log(`Token ACQ`);
    //                         this.msalAuthService.instance.setActiveAccount(response.account);
    //                         this.signInAzureUser(response.accessToken);
    //                     },
    //                     error: (error: any) => {
    //                         this.msalAuthService.acquireTokenRedirect({} as SilentRequest);
    //                     }
    //                 });
    //             return;
    //         }
    //     }
    //     console.log(`Have an active CA user`);
    //     this.loginDone = true;
    //     this.user = this.userService.user();
    //     console.log(`User is ${this.user?.firstName}`);
    // }

    // loginRedirect() {
    //     console.log(`loginRedirect`);
    //     if (this.msalGuardConfig.authRequest) {
    //         this.msalAuthService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
    //     } else {
    //         this.msalAuthService.loginRedirect();
    //     }
    // }

    loginPopup() {
        console.log(`loginPopup`);
        if (this.msalGuardConfig.authRequest) {
            this.msalAuthService.loginPopup({ ...this.msalGuardConfig.authRequest } as PopupRequest)
                .subscribe((response: AuthenticationResult) => {
                    this.msalAuthService.instance.setActiveAccount(response.account);
                });
        } else {
            this.msalAuthService.loginPopup()
                .subscribe((response: AuthenticationResult) => {
                    this.msalAuthService.instance.setActiveAccount(response.account);
                });
        }
    }

    // xxx_signInAzureUser(accessToken: string) {
    //     console.log(`signInAzureUser`);
    //     this.userService
    //         .loginAzure(accessToken)
    //         .subscribe({
    //             next: (success: UserDataResponse) => {
    //                 console.debug(`got azure user: ${JSON.stringify(success)}`);
    //                 this.sendingRequest = false;
    //                 this.loginDone = true;
    //                 const user = this.userService.user();
    //                 if (!user) throw Error(`Did not receive a valid user object!`);
    //             },
    //             error: (error: any) => {
    //                 console.error(error);

    //                 // something unexpected occured
    //                 this.emailAssigned = false;
    //                 this.sendingRequest = false;
    //                 this.router.navigateByUrl('user/user-email-not-assigned');
    //                 //this.loginDone = true;
    //             }
    //         });
    // }

    logout(popup?: boolean) {
        console.log(`logout`);
        this.storage.clearLocal();
        this.authService.logout();
        this.userService.userHasSignedOut();

        if (popup) {
            this.msalAuthService.logoutPopup({
                mainWindowRedirectUri: "/"
            });
        } else {
            this.msalAuthService.logoutRedirect();
        }
    }

    // MARK: Variables

    title = 'Abeona';
    isIframe = false;
    loginDisplay = false;
    loginDone = false;
    emailAssigned = false;
    sendingRequest = false;
    waitTimeDone = false;
    returnUrl = '/';
    userChangeSubscription?: Subscription;

    user: User | undefined;
    showTopMenu = true;

    private readonly _destroying$ = new Subject<void>();
}
